import {
  queryOptions,
  useQuery,
  useSuspenseQuery,
  type UseQueryResult,
  type UseSuspenseQueryResult,
} from '@tanstack/react-query'

import { hasCode } from '@/api/errors'
import { postsKeys } from '@/features/posts/api/keys'
import { LATEST_POSTS, POST_NOT_FOUND, postsService } from '@/features/posts/api/postsService'
import type { PostCard, PostDetail } from '@/schemas/posts'

/** Home's "Latest from MoneyMitra" row. A secondary card read: it draws its own states. */
export const latestPostsQueryOptions = (limit: number = LATEST_POSTS) =>
  queryOptions<PostCard[]>({
    queryKey: postsKeys.latest(limit),
    queryFn: () => postsService.latest(limit),
  })

export const useLatestPosts = (): UseQueryResult<PostCard[]> => useQuery(latestPostsQueryOptions())

/**
 * One post: the article page's main read.
 *
 * Resolves to `null` on `POST_NOT_FOUND` (docs/route-loading.md, "Not-found answers"), so the
 * screen draws its not-found state instead of the route's error page, whose "Try again" would get
 * the same 404 forever. Any other failure still throws.
 */
export const postQueryOptions = (postId: number) =>
  queryOptions<PostDetail | null>({
    queryKey: postsKeys.post(postId),
    queryFn: async () => {
      try {
        return await postsService.post(postId)
      } catch (error) {
        if (hasCode(error, POST_NOT_FOUND)) return null
        throw error
      }
    },
  })

/** The article page's read of it: its loader awaits this exact key. */
export const useSuspensePost = (postId: number): UseSuspenseQueryResult<PostDetail | null> =>
  useSuspenseQuery(postQueryOptions(postId))
